<template>
  <div data-test="sensor-readings-chart">
    <apexchart v-if="hasReadings" :height="180" :options="options" :series="seriesData" />
    <div v-else class="chart-placeholder">
      <span v-if="isLoading">Loading sensor readings…</span>
      <span v-else-if="isError">Unable to load sensor readings</span>
      <span v-else>No sensor readings available for this period</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { type ApexOptions } from 'apexcharts'
import { useAggregateReadingsChartData } from '@/api/sensors'
import { useStore } from 'vuex-composition-helpers'

interface Props {
  sensorIds: string[]
  organizationId: string
  windowStart: Date
  windowEnd: Date
  capacity: number
}

const props = defineProps<Props>()

const store = useStore()
const scadaUnits = computed<string>(() => store.state.organizationConfig.scadaUnits)

const sensorIdsRef = computed(() => props.sensorIds)
const organizationIdRef = computed(() => props.organizationId)
const windowStartRef = computed(() => props.windowStart)
const windowEndRef = computed(() => props.windowEnd)

const {
  data: chartData,
  isLoading,
  isError,
} = useAggregateReadingsChartData(sensorIdsRef, organizationIdRef, windowStartRef, windowEndRef)

const hasReadings = computed(() => (chartData.value?.readings?.length ?? 0) > 0)

const seriesData = computed(() => {
  const readings = chartData.value?.readings ?? []

  return [
    {
      name: 'Tank Level',
      data: readings.map((r) => ({ x: new Date(r.date), y: r.value })),
    },
  ]
})

const options = computed(() => {
  const o: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    tooltip: {
      marker: { show: false },
      x: {
        formatter: (timestamp: number) =>
          new Date(timestamp).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
      },
    },
    xaxis: {
      min: props.windowStart.getTime(),
      max: props.windowEnd.getTime(),
      type: 'datetime',
      labels: { datetimeUTC: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      max: props.capacity,
      tickAmount: 4,
      title: { text: `Level (${scadaUnits.value})` },
      decimalsInFloat: 0,
    },
    title: {
      text: 'Tank Level',
    },
  }

  return o
})
</script>

<style scoped>
.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 180px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}
</style>
